/**
 * Forward migration of player UI settings. v1 was recents only; v2 adds editor chrome prefs;
 * v3 adds render preferences (`render.smoothEdges`, missing → on, the pre-v3 behaviour).
 */

import { clampRgba, isRgba, rgbaEquals, type Rgba } from '../color.ts';
import { clampElevation } from '../map/document.ts';
import { sanitizeMapFilename } from '../map/filename.ts';
import { labelFromFilename, sanitizeMapName } from '../map/name.ts';
import {
  clampTerraceLevels,
  clampTerraceRadius,
  clampTerraceRandomness,
  DEFAULT_TERRACE_COLOR_VARIATION,
  DEFAULT_TERRACE_ELEV_RAND,
  DEFAULT_TERRACE_JITTER,
  DEFAULT_TERRACE_LEVELS,
  DEFAULT_TERRACE_PEAK_M,
  DEFAULT_TERRACE_RADIUS,
  DEFAULT_TERRACE_RADIUS_RAND,
} from '../map/terrace.ts';
import {
  DEFAULT_REFERENCE_IMAGE_ALPHA,
  RECENT_COLOR_LIMIT,
  RECENT_MAP_LIMIT,
  type EditorSettings,
  type RecentMap,
  type RenderSettings,
  type SettingsDocument,
} from './types.ts';
import {
  CURRENT_SETTINGS_VERSION,
  OLDEST_SUPPORTED_SETTINGS_VERSION,
} from './version.ts';

export { CURRENT_SETTINGS_VERSION, OLDEST_SUPPORTED_SETTINGS_VERSION };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** What the editor assumed before these lived in settings. v1 migrate must stamp this. */
export function createDefaultEditorSettings(): EditorSettings {
  return {
    logVisible: false,
    logFilter: 'both',
    showScale: true,
    showBoat: false,
    showMinimap: true,
    showRoutes: true,
    snapToGrid: false,
    previewLighting: false,
    showElevation: false,
    showGrid: false,
    showPerf: false,
    referenceImageAlpha: DEFAULT_REFERENCE_IMAGE_ALPHA,
    terraceLevels: DEFAULT_TERRACE_LEVELS,
    terracePeakM: DEFAULT_TERRACE_PEAK_M,
    terraceRadius: DEFAULT_TERRACE_RADIUS,
    terraceColorVariation: DEFAULT_TERRACE_COLOR_VARIATION,
    terraceJitter: DEFAULT_TERRACE_JITTER,
    terraceElevRand: DEFAULT_TERRACE_ELEV_RAND,
    terraceRadiusRand: DEFAULT_TERRACE_RADIUS_RAND,
    recentMaps: [],
  };
}

/** Canvas MSAA on, as every build before v3 behaved. */
export function createDefaultRenderSettings(): RenderSettings {
  return { smoothEdges: true };
}

export function createEmptySettings(): SettingsDocument {
  return {
    version: CURRENT_SETTINGS_VERSION,
    recentColors: [],
    editor: createDefaultEditorSettings(),
    render: createDefaultRenderSettings(),
  };
}

export function pushRecentColor(colors: readonly Rgba[], incoming: Rgba): Rgba[] {
  const next = clampRgba(incoming);

  return [next, ...colors.filter((color) => !rgbaEquals(color, next))].slice(0, RECENT_COLOR_LIMIT);
}

function catalogMapId(raw: string): string | null {
  const trimmed = raw.trim();

  if (!trimmed || /[/\\]/.test(trimmed)) {
    return null;
  }

  const file = sanitizeMapFilename(trimmed);

  if (!file) {
    return null;
  }

  const id = file.replace(/\.json$/i, '');
  return !id || id === 'Untitled' ? null : id;
}

/** Moves `incoming` to the front and drops older rows past the cap. Untitled never lands here. */
export function pushRecentMap(maps: readonly RecentMap[], incoming: RecentMap): RecentMap[] {
  const id = catalogMapId(incoming.id);

  if (!id) {
    return [...maps];
  }

  const label = incoming.label.trim() ? sanitizeMapName(incoming.label) : labelFromFilename(id);
  return [{ id, label }, ...maps.filter((entry) => entry.id !== id)].slice(0, RECENT_MAP_LIMIT);
}

function migrateRecentColors(value: unknown): Rgba[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const colors: Rgba[] = [];

  for (const entry of value) {
    if (!isRgba(entry)) {
      continue;
    }

    const color = clampRgba(entry);

    if (colors.some((existing) => rgbaEquals(existing, color))) {
      continue;
    }

    colors.push(color);

    if (colors.length >= RECENT_COLOR_LIMIT) {
      break;
    }
  }

  return colors;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function migrateLogFilter(value: unknown): EditorSettings['logFilter'] {
  return value === 'errors' || value === 'warnings' || value === 'both' ? value : 'both';
}

function migrateRecentMaps(value: unknown): RecentMap[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const maps: RecentMap[] = [];
  const seen = new Set<string>();

  for (const entry of value) {
    let id: string | null = null;
    let label = '';

    if (typeof entry === 'string') {
      id = catalogMapId(entry);
      label = id ? labelFromFilename(id) : '';
    } else if (isRecord(entry) && typeof entry.id === 'string') {
      id = catalogMapId(entry.id);
      label =
        typeof entry.label === 'string' && entry.label.trim()
          ? sanitizeMapName(entry.label)
          : id
            ? labelFromFilename(id)
            : '';
    }

    if (!id || seen.has(id)) {
      continue;
    }

    seen.add(id);
    maps.push({ id, label });

    if (maps.length >= RECENT_MAP_LIMIT) {
      break;
    }
  }

  return maps;
}

function migrateEditor(value: unknown): EditorSettings {
  const defaults = createDefaultEditorSettings();

  if (!isRecord(value)) {
    return defaults;
  }

  return {
    logVisible: asBoolean(value.logVisible, defaults.logVisible),
    logFilter: migrateLogFilter(value.logFilter),
    showScale: asBoolean(value.showScale, defaults.showScale),
    showBoat: asBoolean(value.showBoat, defaults.showBoat),
    showMinimap: asBoolean(value.showMinimap, defaults.showMinimap),
    showRoutes: asBoolean(value.showRoutes, defaults.showRoutes),
    snapToGrid: asBoolean(value.snapToGrid, defaults.snapToGrid),
    previewLighting: asBoolean(value.previewLighting, defaults.previewLighting),
    // Yesterday columns were tied to Preview lighting. Keep that for files that
    // predate the independent Elevation toggle.
    showElevation: asBoolean(
      value.showElevation,
      asBoolean(value.previewLighting, defaults.showElevation),
    ),
    showGrid: asBoolean(value.showGrid, defaults.showGrid),
    showPerf: asBoolean(value.showPerf, defaults.showPerf),
    referenceImageAlpha: clampUnit(
      asFiniteNumber(value.referenceImageAlpha, defaults.referenceImageAlpha),
    ),
    terraceLevels: clampTerraceLevels(
      asFiniteNumber(value.terraceLevels, defaults.terraceLevels),
    ),
    terracePeakM: clampElevation(
      asFiniteNumber(value.terracePeakM, defaults.terracePeakM),
    ),
    terraceRadius: clampTerraceRadius(
      asFiniteNumber(value.terraceRadius, defaults.terraceRadius),
    ),
    terraceColorVariation: clampTerraceRandomness(
      asFiniteNumber(value.terraceColorVariation, defaults.terraceColorVariation),
    ),
    terraceJitter: clampTerraceRandomness(
      asFiniteNumber(value.terraceJitter, defaults.terraceJitter),
    ),
    terraceElevRand: clampTerraceRandomness(
      asFiniteNumber(value.terraceElevRand, defaults.terraceElevRand),
    ),
    terraceRadiusRand: clampTerraceRandomness(
      asFiniteNumber(value.terraceRadiusRand, defaults.terraceRadiusRand),
    ),
    recentMaps: migrateRecentMaps(value.recentMaps),
  };
}

export function migrateSettings(value: unknown): SettingsDocument | null {
  if (!isRecord(value)) {
    return null;
  }

  const version = value.version;

  if (
    typeof version !== 'number' ||
    version < OLDEST_SUPPORTED_SETTINGS_VERSION ||
    version > CURRENT_SETTINGS_VERSION
  ) {
    return null;
  }

  return {
    version: CURRENT_SETTINGS_VERSION,
    recentColors: migrateRecentColors(value.recentColors),
    editor: migrateEditor(value.editor),
    render: migrateRender(value.render),
  };
}

function migrateRender(value: unknown): RenderSettings {
  const defaults = createDefaultRenderSettings();

  if (!isRecord(value)) {
    return defaults;
  }

  return {
    smoothEdges: asBoolean(value.smoothEdges, defaults.smoothEdges),
  };
}
